import { CommonModule } from '@angular/common';
import { Component, OnInit, OnDestroy } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatTableDataSource } from '@angular/material/table';
import { MessageHandlerService } from '@core/services/message-handler/message-handler.service';
import { HasAccessDirective } from '@sharedModule/directives/has-access.directive';
import { MaterialModule } from '@sharedModule/material/material.module';
import { ConfirmationCommonDialogData } from '@sharedModule/models/confirmation-dialog.model';
import { ConfirmationDialogService } from '@sharedModule/services/confirmation-dialog/confirmation-dialog.service';
import { Subject, takeUntil } from 'rxjs';
import { AdapterSettingsService } from '../../services/adapter-settings.service';
import { MasterAdapterSettingsInterface } from '../../types/adapter-settings';
import { UpsertMasterAdapterSettingsComponent } from '../upsert-master-adapter-settings/upsert-master-adapter-settings.component';

@Component({
  selector: 'app-master-adapter-settings',
  standalone: true,
  imports: [CommonModule, MaterialModule, HasAccessDirective],
  templateUrl: './master-adapter-settings.component.html',
  styleUrl: './master-adapter-settings.component.scss',
})
export class MasterAdapterSettingsComponent implements OnInit, OnDestroy {
  displayedColumns: string[] = [
    'adapter',
    'adapter_type',
    'created_at',
    'action',
  ];
  dataSource!: MatTableDataSource<MasterAdapterSettingsInterface>;
  #destroy$ = new Subject<void>();

  constructor(
    private dialogRef: MatDialog,
    private messageHandlerService: MessageHandlerService,
    private adapterSettingsService: AdapterSettingsService,
    private confirmationDialogService: ConfirmationDialogService
  ) {}

  ngOnInit(): void {
    this.listMasterAdapterSettings();
  }

  listMasterAdapterSettings(): void {
    this.adapterSettingsService
      .listMasterAdapterSettings()
      .pipe(takeUntil(this.#destroy$))
      .subscribe(x => {
        if (x.success) this.dataSource = new MatTableDataSource(x.response);
      });
  }

  addAdapterSetting(): void {
    const dialogRef = this.dialogRef.open(
      UpsertMasterAdapterSettingsComponent,
      {
        disableClose: true,
        data: {
          isUpdate: false,
        },
      }
    );
    dialogRef.afterClosed().subscribe(result => {
      if (result) this.listMasterAdapterSettings();
    });
  }

  updateAdapterSetting(data: MasterAdapterSettingsInterface): void {
    const dialogRef = this.dialogRef.open(
      UpsertMasterAdapterSettingsComponent,
      {
        disableClose: true,
        data: {
          isUpdate: true,
          code: data.code,
          adapter: data.adapter,
          adapter_type: data.adapter_type,
          description: data.description,
          settings: data.settings,
        },
      }
    );
    dialogRef.afterClosed().subscribe(result => {
      if (result) this.listMasterAdapterSettings();
    });
  }

  async deleteAdapterSetting(
    data: MasterAdapterSettingsInterface
  ): Promise<void> {
    const alertCommonDialogData: ConfirmationCommonDialogData = {
      title: '',
      message: `Are you sure you're going to delete this?`,
      submitButtonText: 'Confirm',
    };
    const isConfirmed =
      await this.confirmationDialogService.openConfirmationDialog(
        alertCommonDialogData
      );
    if (isConfirmed) {
      this.adapterSettingsService
        .deleteMasterAdapterSetting(data.code)
        .pipe(takeUntil(this.#destroy$))
        .subscribe({
          next: x => {
            if (x.success) {
              this.messageHandlerService.show(x.message);
              this.listMasterAdapterSettings();
            }
          },
        });
    }
  }

  ngOnDestroy(): void {
    this.#destroy$.next();
    this.#destroy$.complete();
  }
}
